import { getMeta } from '@helpcenter/meta';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import type { InteractionMetrics } from '@atlaskit/react-ufo/interaction-metrics';
import { init } from '@atlaskit/react-ufo/interaction-metrics-init';
import {
	configure as configureResourceTimings,
	startResourceTimingBuffer,
} from '@atlaskit/react-ufo/resource-timing';
import { configure as configureSsrTiming } from '@atlaskit/react-ufo/ssr';
import traceUFOPageLoad from '@atlaskit/react-ufo/trace-pageload';
import { getEnv, EnvType, getIsPageServerSideRendered } from '../../env';
import { getMark } from '../../perf-markers';
import { getHelpCenterCustomData } from '../additional-payload';
import { assetsConfig } from '../assets-config';
import { getAnalyticsWebClientPromise } from '../client';
import { retrieveAndSetPageLoadTrace } from '../retrieve-and-set-page-load-trace';

function runConfigureSsrTiming() {
	configureSsrTiming({
		getDoneMark: () => getMark('ssr.spa.rendered')?.startTime ?? null,
		getTimings: () => null,
		getFeatureFlags: () => null,
	});
}

function getAdditionalHelpCenterFields(interaction: InteractionMetrics) {
	try {
		return { jira: getHelpCenterCustomData(interaction) };
	} catch (error) {
		return {};
	}
}

export const initUFO = () => {
	runConfigureSsrTiming();

	startResourceTimingBuffer();

	retrieveAndSetPageLoadTrace();

	const isStagingEnv = getEnv().environment === EnvType.STAGING;
	configureResourceTimings({
		sanitiseEndpoints(url) {
			return url;
		},
		mapResources(url) {
			const parts = url.split('/');
			const chunkName = parts[parts.length - 1];
			if (chunkName.includes('vendor-react')) return chunkName;
			return url;
		},
	});
	init(
		Promise.resolve({
			getAnalyticsWebClientPromise,
		}),
		{
			product: 'jira',
			region: getMeta('ajs-region') || 'unknown', //TODO:SSR and FF data
			namePrefix: 'help-center-ufo-',
			ssr: {
				getSSRDoneTime: () => getMark('ssr.spa.rendered')?.startTime,
			},
			assetsConfig,
			additionalPayloadData: getAdditionalHelpCenterFields,
			capability: {
				/*
				 * Enabling feature flag data to be captured in UFO.
				 */
				feature_flag_access: 1,
				/*
				 * https://atlassian.slack.com/archives/C01AY8132S3/p1723030532244489?thread_ts=1722580624.223629&cid=C01AY8132S3
				 * react_profiler is added since it is required by the type,
				 * will be cleaned up in the future
				 */
				react_profiler: 0,
			},
			vc: {
				/*
				 * https://stash.atlassian.com/projects/ATLASSIAN/repos/atlassian-frontend-monorepo/pull-requests/129126/diff#platform%2F.changeset%2Fred-lemons-mate.md
				 */
				stopVCAtInteractionFinish: true,
				enabled: true,
				selectorConfig: {
					className: true,
					role: isStagingEnv,
					id: isStagingEnv,
					testId: isStagingEnv,
				},
				includeSSRInV3: FeatureGates.checkGate('help_center_add_ssr_to_ttvc_v3'),
				includeSSRRatio: FeatureGates.checkGate('help_center_add_ssr_ratio_to_ttvc'),
				ssr: getIsPageServerSideRendered(),
				devToolsEnabled: isStagingEnv,
				enabledVCRevisions: {
					all: FeatureGates.checkGate('ufo_disable_ttvc_v4') ? [] : ['fy26.04'],
				},
			},
			kind: {
				/*
				 * Sampling works in 1 in the rate specified.
				 * If rate is 1, it captures all events - 1/1
				 */
				page_load: 1,
				transition: 1,
				press: 1,
				typing: 1,
				legacy: 0, //For some Jira events that will be cleaned up in UFO
				hover: 1,
			},
			postInteractionLog: {
				enabled: true,
				rates: {
					'request-details': 1,
					'request-create': 1,
					login: 1,
				},
			},
		},
	);

	/*
	 * Start instrumenting the page load event.
	 * We invoke `traceUFOPageLoad` once more
	 * when we have the route context.
	 * It attaches the route name to the event.
	 * */
	traceUFOPageLoad();
};
